import type { ReactElement } from "react";

import type { GateInstanceRow } from "~/neucleos/gateInstancesClient";
import {
  useConversationOfApproval,
  useWaitingApprovals,
  type CloudGateWait,
  type WaitingApprovals,
} from "~/neucleos/cloudConversations";
import { CloudGateCard } from "./CloudGateCard";

/**
 * Step 7.4.4a-2b (steps-5-7 design §12d): a card-only approval of one of the person's World conversations, in the Inbox of a
 * window with no Mac (cleo-web). Its card is that conversation's own: the same CloudGateCard the conversation pane shows,
 * for the World's own verified wait (pocketCloudDoors:waitingApprovals), decided through the same door Pocket uses
 * (pocketCloudDoors:decideGate), where the World checks the exact occurrence and call, re-reads a Members or price change
 * and supplies the card itself. One door and one card for Pocket, the pane and this Inbox.
 *
 * The hook answers null when the approval is not one this card shows (asked with a null row, another person's or a Desk's
 * approval, or not the conversation's current wait): the Inbox then shows its own card, where only Decline is offered.
 */
export function useInboxCloudCard(
  worldKey: string | null,
  row: GateInstanceRow | null,
): {
  readonly conversationId: string;
  readonly wait: CloudGateWait;
  readonly waiting: WaitingApprovals;
} | null {
  const owner = useConversationOfApproval(
    row === null ? null : worldKey,
    row?.gateInstanceKey ?? null,
  );
  const conversationId =
    owner.status === "ready" && owner.value !== null ? owner.value.conversationId : null;
  const waiting = useWaitingApprovals(conversationId);
  if (row === null || conversationId === null || waiting.status !== "ready") return null;
  const wait = currentWaitOf(waiting.value, row);
  return wait === null ? null : { conversationId, wait, waiting: waiting.value };
}

/** The conversation's own card, for what useInboxCloudCard found. */
export function InboxCloudCard({
  card,
}: {
  readonly card: NonNullable<ReturnType<typeof useInboxCloudCard>>;
}): ReactElement {
  return (
    <div data-cleo="inbox-cloud-card" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <CloudGateCard conversationId={card.conversationId} wait={card.wait} waiting={card.waiting} />
    </div>
  );
}

/**
 * The conversation's current wait, as the card's wait, when it is exactly this Inbox row's approval (its key and digest);
 * otherwise null. Built from the World's verified snapshot alone (the call it read and checked), never from the row.
 */
export function currentWaitOf(
  waiting: WaitingApprovals,
  row: GateInstanceRow,
): CloudGateWait | null {
  const current = waiting.current ?? null;
  if (
    current === null ||
    current.approval.gateInstanceKey !== row.gateInstanceKey ||
    current.approval.targetDigest !== row.targetDigest
  )
    return null;
  return {
    seq: current.waitSeq,
    toolCallId: current.occurrence.toolCallId ?? "",
    name: current.call.name,
    digest: current.call.digest,
    text: "",
    turnKey: current.occurrence.turnKey,
    step: current.occurrence.step,
    argumentsJson: current.call.argumentsJson,
  };
}
